/**
 * Хук поиска лицевого счёта по номеру ЛС для неавторизованного пользователя.
 *
 * Работает так же, как `useGuestAccountSearch`: ищет автоматически с debounce,
 * как только номер валиден (`isValidAccountNumberParam`), и использует react-query.
 * Кеш отделён от поиска по телефону: ключ `['account-number-search', номер]`.
 * Возвращает ту же форму результата, что и `useGuestAccountSearch`.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { searchAccount, AccountSearchResponse } from '../api/payment.api';
import { isValidAccountNumberParam } from '../utils/paymentPrefill';
import type { UseGuestAccountSearchResult } from './useGuestAccountSearch';

/**
 * Результат работы хука поиска счёта по номеру ЛС.
 * Совпадает с результатом `useGuestAccountSearch` плюс признак ошибки запроса.
 */
export interface UseAccountNumberSearchResult extends UseGuestAccountSearchResult {
  /** Запрос завершился ошибкой, отличной от «счёт не найден» (сеть, сервер, лимиты) */
  isError: boolean;
}

/**
 * Ищет счёт по номеру ЛС. Ответ 404 («Счёт не найден») — штатный результат,
 * поэтому превращается в пустой список, а не в ошибку запроса.
 *
 * @param accountNumber - номер лицевого счёта (уже обрезанный по краям)
 * @returns ответ поиска; для 404 — `{ accounts: [] }`
 * @throws любые ошибки, кроме 404
 */
const fetchByAccountNumber = async (accountNumber: string): Promise<AccountSearchResponse> => {
  try {
    return await searchAccount(accountNumber);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { accounts: [] };
    }
    throw error;
  }
};

/**
 * Хук автоматического поиска счёта по номеру ЛС с debounce.
 *
 * Запрос включается только для валидного номера (после trim). Для пустого,
 * `undefined` или невалидного значения запрос не выполняется.
 *
 * @param accountNumber - введённый номер лицевого счёта (может отсутствовать)
 * @param debounceMs - задержка перед запросом после последнего изменения (мс)
 * @returns найденные счета и состояние поиска
 *
 * @example
 * ```tsx
 * const { accounts, isSearching, notFound, isError } = useAccountNumberSearch(accountNumber);
 * ```
 */
export const useAccountNumberSearch = (
  accountNumber: string | undefined,
  debounceMs = 500
): UseAccountNumberSearchResult => {
  // Debounce номера ЛС, чтобы не слать запрос на каждое нажатие
  const [debouncedAccountNumber, setDebouncedAccountNumber] = useState('');

  useEffect(() => {
    const normalized = isValidAccountNumberParam(accountNumber) ? (accountNumber as string).trim() : '';
    const timer = setTimeout(() => setDebouncedAccountNumber(normalized), debounceMs);
    return () => clearTimeout(timer);
  }, [accountNumber, debounceMs]);

  const enabled = debouncedAccountNumber.length > 0;

  const { data, isFetching, isError } = useQuery({
    // Отдельный ключ, чтобы не пересекаться с ['guest-account-search', phone]
    queryKey: ['account-number-search', debouncedAccountNumber],
    queryFn: () => fetchByAccountNumber(debouncedAccountNumber),
    enabled,
    // Счета редко меняются в рамках сессии оплаты — кэшируем на минуту
    staleTime: 60_000,
    retry: false,
  });

  const accounts = data?.accounts ?? [];

  return {
    accounts,
    isSearching: enabled && isFetching,
    notFound: enabled && !isFetching && !isError && data !== undefined && accounts.length === 0,
    isError: enabled && isError,
  };
};
